import { useState, useCallback, useMemo, useEffect } from 'react';
import { X, Shuffle, RotateCcw, ChevronRight, ChevronDown, Hand, Layers, Eye, EyeOff, Sparkles, Timer, TrendingUp, Target } from 'lucide-react';
import { usePlaytesterSync } from '../lib/playtester-sync';
import { overlayService } from '../lib/supabase';

interface PlaytesterCard {
    name: string;
    quantity: number;
    imageUrl?: string;
    backImageUrl?: string;
    cmc: number;
    isLand: boolean;
    colors: string[];
    type?: string;
}

// Plan configurations: { cardName: { base: qty, A: qty, B: qty, ..., mvs?: { A: [...], B: [...] } } }
type PlanConfig = Record<string, {
    base?: number;
    A?: number;
    B?: number;
    C?: number;
    D?: number;
    E?: number;
    mvs?: {
        A?: number[];
        B?: number[];
        C?: number[];
        D?: number[];
        E?: number[];
    };
}>;

interface PlaytesterProps {
    deck: PlaytesterCard[];
    sideboard?: PlaytesterCard[];
    plans?: PlanConfig | null;
    deckName?: string;
    onClose: () => void;
    // 'goldfish' = private solo testing (user sees everything, no broadcast)
    // 'session' = live session mode (user sees their hand, but broadcast hides hand from viewers)
    mode?: 'goldfish' | 'session';
    // Custom sleeve colors - array of MTG color pips (W, U, B, R, G) for gradient backs
    sleeveColors?: string[];
}

// Generate gradient CSS from sleeve colors
const getSleeveGradient = (colors: string[]): string => {
    if (!colors || colors.length === 0) return 'linear-gradient(135deg, #1a1a2e 0%, #16213e 100%)';

    const colorMap: Record<string, string> = {
        'W': '#f8e7b9', // White
        'U': '#0e68ab', // Blue
        'B': '#1a1a1a', // Black
        'R': '#d3202a', // Red
        'G': '#00733e', // Green
    };

    const gradientColors = colors.map(c => colorMap[c.toUpperCase()] || '#444');

    if (gradientColors.length === 1) {
        return `linear-gradient(135deg, ${gradientColors[0]}99 0%, ${gradientColors[0]}66 100%)`;
    }

    const stops = gradientColors.map((c, i) => `${c}88 ${(i / (gradientColors.length - 1)) * 100}%`);
    return `linear-gradient(135deg, ${stops.join(', ')})`;
};

type Zone = 'library' | 'hand' | 'battlefield' | 'graveyard' | 'exile';
type PlanId = 'base' | 'A' | 'B' | 'C' | 'D' | 'E';

// Counter types commonly used in MTG
type CounterType = '+1/+1' | '-1/-1' | 'loyalty' | 'charge' | 'time' | 'quest' | 'custom';

interface CardInstance {
    id: string;
    card: PlaytesterCard;
    zone: Zone;
    tapped: boolean;
    faceDown: boolean;
    // Enhanced battlefield features
    position?: { x: number; y: number }; // Free-form position on battlefield
    counters?: Record<CounterType | string, number>; // Counter types and amounts
    isToken?: boolean; // True if this is a created token
    isCopy?: boolean; // True if this is a copy of another card
    summoningSickness?: boolean; // For tracking if creature just entered
    attachedTo?: string; // ID of card this is attached to (auras/equipment)
    attachments?: string[]; // IDs of cards attached to this
    flipped?: boolean; // For flip cards
    transformed?: boolean; // For DFCs showing back face
}

export function DeckPlaytester({ deck, sideboard = [], plans, deckName = 'Deck', onClose, mode = 'goldfish', sleeveColors = [] }: PlaytesterProps) {
    // Plan selection
    const [selectedPlan, setSelectedPlan] = useState<PlanId>('base');
    const [showPlanSelector, setShowPlanSelector] = useState(!!plans && Object.keys(plans).length > 0);

    // Compute deck with selected plan applied (quantities AND MVs)
    const effectiveDeck = useMemo(() => {
        if (!plans || selectedPlan === 'base') {
            return deck;
        }

        // For non-base plans, start with the mainboard deck only
        // Then add in sideboard cards that have explicit plan quantities > 0
        const planKey = selectedPlan as 'A' | 'B' | 'C' | 'D' | 'E';

        // Process mainboard cards - they keep their quantities unless explicitly overridden
        const mainCards = deck.map(card => {
            const cardPlan = plans[card.name];
            let modifiedCard = { ...card };

            if (cardPlan) {
                // Check for explicit quantity override in this plan
                if (cardPlan[planKey] !== undefined) {
                    const newQty = cardPlan[planKey]!;
                    if (newQty !== card.quantity) {
                        console.log(`[Plan Debug] ${card.name}: qty ${card.quantity} -> ${newQty} (explicit override)`);
                    }
                    modifiedCard.quantity = newQty;
                }
                // If no plan quantity override, keep original card quantity (don't use base!)

                // Apply MV overrides if present
                if (cardPlan.mvs && cardPlan.mvs[planKey]) {
                    const mvArray = cardPlan.mvs[planKey]!;
                    if (mvArray.length > 0) {
                        const avgMV = mvArray.reduce((sum, mv) => sum + mv, 0) / mvArray.length;
                        modifiedCard.cmc = avgMV;
                    }
                }
            }

            return modifiedCard;
        }).filter(card => card.quantity > 0);

        // Process sideboard cards - only include if they have explicit plan quantity > 0
        const sideCards = (sideboard || []).map(card => {
            const cardPlan = plans[card.name];
            if (!cardPlan) return null;

            const planQty = cardPlan[planKey];
            if (planQty === undefined || planQty <= 0) return null;

            let modifiedCard = { ...card, quantity: planQty };

            // Apply MV overrides if present
            if (cardPlan.mvs && cardPlan.mvs[planKey]) {
                const mvArray = cardPlan.mvs[planKey]!;
                if (mvArray.length > 0) {
                    const avgMV = mvArray.reduce((sum, mv) => sum + mv, 0) / mvArray.length;
                    modifiedCard.cmc = avgMV;
                }
            }

            return modifiedCard;
        }).filter((card): card is PlaytesterCard => card !== null);

        // Combine same-name cards (e.g., Get Lost in main + Get Lost from side)
        const combined = [...mainCards, ...sideCards];
        const cardMap = new Map<string, PlaytesterCard>();

        combined.forEach(card => {
            const existing = cardMap.get(card.name);
            if (existing) {
                // Same card appears in both - add quantities
                cardMap.set(card.name, { ...existing, quantity: existing.quantity + card.quantity });
            } else {
                cardMap.set(card.name, card);
            }
        });

        return Array.from(cardMap.values());
    }, [deck, sideboard, plans, selectedPlan]);

    // Game state
    const [library, setLibrary] = useState<CardInstance[]>([]);
    const [hand, setHand] = useState<CardInstance[]>([]);
    const [battlefield, setBattlefield] = useState<CardInstance[]>([]);
    const [graveyard, setGraveyard] = useState<CardInstance[]>([]);
    const [exile, setExile] = useState<CardInstance[]>([]);

    // Game tracking
    const [turn, setTurn] = useState(0);
    const [mulliganCount, setMulliganCount] = useState(0);
    const [landDropsThisTurn, setLandDropsThisTurn] = useState(0);
    const [gameStarted, setGameStarted] = useState(false);
    const [showLibraryTop, setShowLibraryTop] = useState(false);

    // London Mulligan state: draw -> decide (keep/mull) -> choose (put cards on bottom) -> done
    const [mulliganPhase, setMulliganPhase] = useState<'draw' | 'decide' | 'choose' | 'done'>('draw');
    const [cardsToBottom, setCardsToBottom] = useState<string[]>([]); // Card IDs selected to put on bottom
    const [dragHandIndex, setDragHandIndex] = useState<number | null>(null); // For hand reordering

    // Mana tracking
    const [floatingMana, setFloatingMana] = useState<Record<string, number>>({ W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 });

    // Deck composition tracking (for PDR / deck thinning)
    const [initialDeckSize, setInitialDeckSize] = useState(0);
    const [initialLandCount, setInitialLandCount] = useState(0);

    // UI state
    const [selectedCard, setSelectedCard] = useState<CardInstance | null>(null);
    const [hoveredCard, setHoveredCard] = useState<CardInstance | null>(null);
    const [showStats, setShowStats] = useState(true);

    // Enhanced battlefield controls
    const [battlefieldZoom, setBattlefieldZoom] = useState(1); // 0.5 to 2.0
    const [draggedCard, setDraggedCard] = useState<CardInstance | null>(null);
    const [contextMenuCard, setContextMenuCard] = useState<{ card: CardInstance; x: number; y: number } | null>(null);
    const [nextTokenId, setNextTokenId] = useState(10000); // ID counter for tokens

    // Overlay sync hook
    const { startSession, updateSession, endSession } = usePlaytesterSync();

    // Extract unique deck colors
    const deckColors = useMemo(() => {
        const colors = new Set<string>();
        effectiveDeck.forEach(card => {
            card.colors?.forEach(c => colors.add(c));
        });
        return Array.from(colors);
    }, [effectiveDeck]);

    // Create card instances from deck
    const createDeck = useCallback(() => {
        const instances: CardInstance[] = [];
        let idCounter = 0;

        effectiveDeck.forEach(card => {
            for (let i = 0; i < card.quantity; i++) {
                instances.push({
                    id: `card-${idCounter++}`,
                    card,
                    zone: 'library',
                    tapped: false,
                    faceDown: false,
                });
            }
        });

        return instances;
    }, [effectiveDeck]);

    // Fisher-Yates shuffle
    const shuffleArray = <T,>(array: T[]): T[] => {
        const shuffled = [...array];
        for (let i = shuffled.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
        }
        return shuffled;
    };

    // Initialize game
    const startNewGame = useCallback(() => {
        const newDeck = shuffleArray(createDeck());
        setLibrary(newDeck);
        setHand([]);
        setBattlefield([]);
        setGraveyard([]);
        setExile([]);
        setTurn(0);
        setMulliganCount(0);
        setLandDropsThisTurn(0);
        setGameStarted(false);
        setFloatingMana({ W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 });
        setSelectedCard(null);
        // Capture initial deck composition for thinning calculation
        setInitialDeckSize(newDeck.length);
        setInitialLandCount(newDeck.filter(c => c.card.isLand).length);
    }, [createDeck]);

    // Draw opening hand - LONDON MULLIGAN: Always draw 7, then decide keep/mull
    const drawOpeningHand = useCallback(() => {
        const drawnCards = library.slice(0, 7);
        const remainingLibrary = library.slice(7);

        setHand(drawnCards.map(c => ({ ...c, zone: 'hand' as Zone })));
        setLibrary(remainingLibrary);
        setCardsToBottom([]);

        // Always go to 'decide' phase first - user chooses keep or mull
        setMulliganPhase('decide');
    }, [library]);

    // User decides to keep their hand
    const keepHand = useCallback(() => {
        if (mulliganCount > 0) {
            // Need to choose cards to put on bottom
            setMulliganPhase('choose');
        } else {
            // No mulligans - start the game
            setMulliganPhase('done');
            setGameStarted(true);
            setTurn(1);

            if (mode === 'session') {
                startSession({
                    deckName,
                    deckColors,
                    libraryCount: library.length,
                    handCount: hand.length,
                    battlefieldCount: 0,
                    graveyardCount: 0,
                    exileCount: 0,
                    turnNumber: 1,
                    lifeTotal: 20,
                });
            }
        }
    }, [mulliganCount, mode, deckName, deckColors, startSession, library.length, hand.length]);

    // Confirm London Mulligan - put selected cards on bottom
    const confirmMulligan = useCallback(() => {
        if (cardsToBottom.length !== mulliganCount) return; // Must select exactly X cards

        // Move selected cards from hand to bottom of library
        const cardsGoingBottom = hand.filter(c => cardsToBottom.includes(c.id));
        const remainingHand = hand.filter(c => !cardsToBottom.includes(c.id));

        setHand(remainingHand);
        setLibrary(prev => [...prev, ...cardsGoingBottom.map(c => ({ ...c, zone: 'library' as Zone }))]);
        setCardsToBottom([]);
        setMulliganPhase('done');
        setGameStarted(true);
        setTurn(1);

        // Start overlay sync
        if (mode === 'session') {
            startSession({
                deckName,
                deckColors,
                libraryCount: library.length + cardsGoingBottom.length,
                handCount: remainingHand.length,
                battlefieldCount: 0,
                graveyardCount: 0,
                exileCount: 0,
                turnNumber: 1,
                lifeTotal: 20,
            });
        }
    }, [hand, cardsToBottom, mulliganCount, library, mode, deckName, deckColors, startSession]);

    // Toggle card for putting on bottom during mulligan
    const toggleCardForBottom = useCallback((cardId: string) => {
        setCardsToBottom(prev => {
            if (prev.includes(cardId)) {
                return prev.filter(id => id !== cardId);
            } else if (prev.length < mulliganCount) {
                return [...prev, cardId];
            }
            return prev; // Can't add more than mulliganCount
        });
    }, [mulliganCount]);

    // Mulligan - shuffle hand back and increment count
    const mulligan = useCallback(() => {
        if (mulliganCount >= 6) return; // Can't mulligan to 0

        // Put hand back and reshuffle
        const allCards = shuffleArray([...library, ...hand.map(c => ({ ...c, zone: 'library' as Zone }))]);
        setLibrary(allCards);
        setHand([]);
        setMulliganCount(prev => prev + 1);
        setGameStarted(false);
        setMulliganPhase('draw');
        setCardsToBottom([]);
    }, [library, hand, mulliganCount]);

    // Draw a card
    const drawCard = useCallback((count = 1) => {
        if (library.length < count) return;

        const drawnCards = library.slice(0, count);
        const remainingLibrary = library.slice(count);

        setHand(prev => [...prev, ...drawnCards.map(c => ({ ...c, zone: 'hand' as Zone }))]);
        setLibrary(remainingLibrary);
    }, [library]);

    // Next turn
    const nextTurn = useCallback(() => {
        setTurn(prev => prev + 1);
        setLandDropsThisTurn(0);
        setFloatingMana({ W: 0, U: 0, B: 0, R: 0, G: 0, C: 0 });

        // Untap all AND clear summoning sickness (permanents have now survived a full turn cycle)
        setBattlefield(prev => prev.map(c => ({
            ...c,
            tapped: false,
            summoningSickness: false
        })));

        // Draw for turn (skip turn 1)
        if (turn >= 1) {
            drawCard(1);
        }
    }, [turn, drawCard]);

    // Play card from hand to battlefield
    const playCard = useCallback((cardInstance: CardInstance, options?: { tapped?: boolean }) => {
        if (cardInstance.zone !== 'hand') return;

        // Check land drop
        if (cardInstance.card.isLand) {
            if (landDropsThisTurn >= 1) {
                // Could show warning that land already played
                return;
            }
            setLandDropsThisTurn(prev => prev + 1);
        }

        // Determine if this is a creature (check type string for 'creature')
        const isCreature = cardInstance.card.type?.toLowerCase().includes('creature') ?? false;

        setHand(prev => prev.filter(c => c.id !== cardInstance.id));
        setBattlefield(prev => [...prev, {
            ...cardInstance,
            zone: 'battlefield' as Zone,
            tapped: options?.tapped ?? false,
            // Creatures get summoning sickness when entering battlefield
            summoningSickness: isCreature ? true : undefined
        }]);
    }, [landDropsThisTurn]);

    // Tap/untap card
    const toggleTap = useCallback((cardInstance: CardInstance) => {
        if (cardInstance.zone !== 'battlefield') return;

        setBattlefield(prev => prev.map(c =>
            c.id === cardInstance.id ? { ...c, tapped: !c.tapped } : c
        ));
    }, []);

    // Move card to graveyard
    const toGraveyard = useCallback((cardInstance: CardInstance) => {
        const removeFrom = cardInstance.zone;
        const setters: Record<Zone, React.Dispatch<React.SetStateAction<CardInstance[]>>> = {
            library: setLibrary,
            hand: setHand,
            battlefield: setBattlefield,
            graveyard: setGraveyard,
            exile: setExile,
        };

        setters[removeFrom](prev => prev.filter(c => c.id !== cardInstance.id));
        setGraveyard(prev => [...prev, { ...cardInstance, zone: 'graveyard' as Zone, tapped: false }]);
    }, []);

    // Move card to exile
    const toExile = useCallback((cardInstance: CardInstance) => {
        const removeFrom = cardInstance.zone;
        const setters: Record<Zone, React.Dispatch<React.SetStateAction<CardInstance[]>>> = {
            library: setLibrary,
            hand: setHand,
            battlefield: setBattlefield,
            graveyard: setGraveyard,
            exile: setExile,
        };

        setters[removeFrom](prev => prev.filter(c => c.id !== cardInstance.id));
        setExile(prev => [...prev, { ...cardInstance, zone: 'exile' as Zone, tapped: false }]);
    }, []);

    // Return card to hand
    const toHand = useCallback((cardInstance: CardInstance) => {
        const removeFrom = cardInstance.zone;
        const setters: Record<Zone, React.Dispatch<React.SetStateAction<CardInstance[]>>> = {
            library: setLibrary,
            hand: setHand,
            battlefield: setBattlefield,
            graveyard: setGraveyard,
            exile: setExile,
        };

        setters[removeFrom](prev => prev.filter(c => c.id !== cardInstance.id));
        setHand(prev => [...prev, { ...cardInstance, zone: 'hand' as Zone, tapped: false }]);
    }, []);

    // Add/remove counters from a card
    const modifyCounter = useCallback((cardId: string, counterType: string, delta: number) => {
        setBattlefield(prev => prev.map(c => {
            if (c.id !== cardId) return c;
            const counters = { ...(c.counters || {}) };
            counters[counterType] = Math.max(0, (counters[counterType] || 0) + delta);
            if (counters[counterType] === 0) delete counters[counterType];
            return { ...c, counters };
        }));
    }, []);

    // Create a token copy of a card
    const createToken = useCallback((sourceCard: CardInstance) => {
        const tokenId = `token-${nextTokenId}`;
        setNextTokenId(prev => prev + 1);

        const token: CardInstance = {
            id: tokenId,
            card: { ...sourceCard.card, name: `${sourceCard.card.name} (Token)` },
            zone: 'battlefield',
            tapped: false,
            faceDown: false,
            isToken: true,
            position: sourceCard.position
                ? { x: sourceCard.position.x + 30, y: sourceCard.position.y + 30 }
                : undefined,
        };

        setBattlefield(prev => [...prev, token]);
    }, [nextTokenId]);

    // Create a copy of a card (not a token - for Clone effects)
    const createCopy = useCallback((sourceCard: CardInstance) => {
        const copyId = `copy-${nextTokenId}`;
        setNextTokenId(prev => prev + 1);

        const copy: CardInstance = {
            id: copyId,
            card: { ...sourceCard.card, name: `${sourceCard.card.name} (Copy)` },
            zone: 'battlefield',
            tapped: false,
            faceDown: false,
            isCopy: true,
            position: sourceCard.position
                ? { x: sourceCard.position.x + 40, y: sourceCard.position.y + 40 }
                : undefined,
        };

        setBattlefield(prev => [...prev, copy]);
    }, [nextTokenId]);

    // Update card position on battlefield (for drag-and-drop)
    const updateCardPosition = useCallback((cardId: string, x: number, y: number) => {
        setBattlefield(prev => prev.map(c =>
            c.id === cardId ? { ...c, position: { x, y } } : c
        ));
    }, []);

    // Stats calculations
    const stats = useMemo(() => {
        const landsInPlay = battlefield.filter(c => c.card.isLand).length;
        const landsInHand = hand.filter(c => c.card.isLand).length;
        const spellsInHand = hand.filter(c => !c.card.isLand).length;
        const cardsDrawn = effectiveDeck.reduce((sum, c) => sum + c.quantity, 0) - library.length;
        const landsRemaining = library.filter(c => c.card.isLand).length;
        const totalRemaining = library.length;

        // Probability of drawing a land next
        const landDrawProb = totalRemaining > 0 ? (landsRemaining / totalRemaining) * 100 : 0;

        // Count untapped lands
        const untappedLands = battlefield.filter(c => c.card.isLand && !c.tapped).length;

        // EMA (Effective Mana Available): 
        // = untapped lands + mana dorks that can tap (not summoning sick)
        // A creature can tap for mana if: untapped, has mana ability, and not summoning sick
        const manaDorks = battlefield.filter(c => {
            if (c.card.isLand) return false; // Lands counted separately
            if (c.tapped) return false; // Can't tap if already tapped
            if (c.summoningSickness) return false; // Summoning sick creatures can't tap
            // Check if it's a creature with mana ability (simplified: check type for common mana dorks)
            const type = c.card.type?.toLowerCase() || '';
            const isManaCreature = type.includes('creature') && (
                c.card.name.includes('Llanowar') ||
                c.card.name.includes('Elves') ||
                c.card.name.includes('Bird') ||
                c.card.name.includes('Mystic') ||
                c.card.name.includes('Hierarch') ||
                c.card.name.includes('Dork') ||
                type.includes('mana')
            );
            return isManaCreature;
        }).length;

        // EMA = lands + available mana dorks (each produces ~1 mana)
        const effectiveManaAvailable = untappedLands + manaDorks;

        // Cards by CMC in hand
        const handByCmc: Record<number, number> = {};
        hand.forEach(c => {
            if (!c.card.isLand) {
                const cmc = Math.min(c.card.cmc, 7);
                handByCmc[cmc] = (handByCmc[cmc] || 0) + 1;
            }
        });

        // Castable spells (cmc <= EMA, not just land count)
        const castableSpells = hand.filter(c => !c.card.isLand && c.card.cmc <= effectiveManaAvailable).length;

        // Deck Thinning Metrics (PDR calculation)
        // Initial land density vs current land density
        const initialLandDensity = initialDeckSize > 0
            ? (initialLandCount / initialDeckSize) * 100
            : 0;
        const currentLandDensity = totalRemaining > 0
            ? (landsRemaining / totalRemaining) * 100
            : 0;

        // Deck thinning = how many lands removed from library vs expected
        // (lands drawn + lands played + lands tutored to battlefield)
        const landsOnBattlefield = battlefield.filter(c => c.card.isLand).length;
        const landsInGraveyard = graveyard.filter(c => c.card.isLand).length;
        const landsInExile = exile.filter(c => c.card.isLand).length;
        const landsKnown = landsInPlay + landsInHand + landsRemaining + landsInGraveyard + landsInExile;
        const deckThinning = initialLandCount - landsKnown; // Positive = lands removed from deck (via tutor effects)

        // Ramp Diagnostics: Potential / Realized / Missed
        // Potential Ramp = lands in play (ideally should equal turn number on curve)
        // Realized Ramp = actual EMA (includes mana dorks)
        // Missed Ramp = ramp cards in hand that we could have played but didn't/couldn't

        // Count ramp cards in hand (by checking rampType or rampCategory)
        const rampCardsInHand = hand.filter(c => {
            const type = c.card.type?.toLowerCase() || '';
            const name = c.card.name.toLowerCase();
            // Check for common ramp indicators
            return (
                name.includes('llanowar') ||
                name.includes('elvish mystic') ||
                name.includes('birds of paradise') ||
                name.includes('sol ring') ||
                name.includes('mana crypt') ||
                name.includes('signet') ||
                name.includes('talisman') ||
                name.includes('rampant growth') ||
                name.includes('cultivate') ||
                name.includes('farseek') ||
                name.includes('nature\'s lore') ||
                type.includes('mana') ||
                (type.includes('creature') && (name.includes('dork') || name.includes('mana')))
            );
        }).length;

        // Count ramp cards on battlefield (producing mana)
        const rampSourcesInPlay = battlefield.filter(c => {
            if (c.card.isLand) return false; // Lands don't count as "ramp sources"
            const type = c.card.type?.toLowerCase() || '';
            return type.includes('artifact') || (type.includes('creature') && !c.summoningSickness);
        }).length;

        // Missed ramp = ramp cards in hand that cost <= available mana
        const missedRamp = hand.filter(c => {
            const name = c.card.name.toLowerCase();
            const isRamp = name.includes('llanowar') || name.includes('mystic') ||
                name.includes('sol ring') || name.includes('signet') ||
                name.includes('rampant') || name.includes('cultivate');
            return isRamp && c.card.cmc <= effectiveManaAvailable;
        }).length;

        return {
            landsInPlay,
            landsInHand,
            spellsInHand,
            cardsDrawn,
            landsRemaining,
            totalRemaining,
            landDrawProb,
            untappedLands,
            manaDorks,
            effectiveManaAvailable,
            handByCmc,
            castableSpells,
            // Deck composition tracking
            initialDeckSize,
            initialLandCount,
            initialLandDensity,
            currentLandDensity,
            deckThinning,
            // Ramp diagnostics
            rampCardsInHand,
            rampSourcesInPlay,
            missedRamp,
        };
    }, [battlefield, hand, library, graveyard, exile, effectiveDeck, initialDeckSize, initialLandCount]);

    // Initialize on mount
    useEffect(() => {
        startNewGame();
    }, [startNewGame]);

    // Sync state to overlay whenever zones change - ONLY in session mode
    // Hand privacy: we sync hand COUNT but never card identities
    useEffect(() => {
        if (gameStarted && mode === 'session') {
            updateSession({
                libraryCount: library.length,
                handCount: hand.length, // Count only, identities stay private
                battlefieldCount: battlefield.length,
                graveyardCount: graveyard.length,
                exileCount: exile.length,
                turnNumber: turn,
            });
        }
    }, [library.length, hand.length, battlefield.length, graveyard.length, exile.length, turn, gameStarted, updateSession, mode]);

    // Broadcast currently hovered card to overlay - ONLY in session mode
    // Hand, library, and face-down cards stay private (never broadcast)
    useEffect(() => {
        if (mode !== 'session') return; // No broadcast in goldfish mode

        // Only broadcast public zones: battlefield (face-up), graveyard, exile
        const isPublicZone = hoveredCard && (
            (hoveredCard.zone === 'battlefield' && !hoveredCard.faceDown) ||
            hoveredCard.zone === 'graveyard' ||
            hoveredCard.zone === 'exile'
        );

        if (isPublicZone && hoveredCard) {
            overlayService.broadcastCard({
                name: hoveredCard.card.name,
                imageUrl: hoveredCard.card.imageUrl || '',
                cmc: hoveredCard.card.cmc,
                type: hoveredCard.card.type,
            });
        } else {
            // Clear preview when hovering private zones (hand, library, face-down)
            overlayService.broadcastCard(null);
        }
    }, [hoveredCard, mode]);

    // Keyboard shortcuts for card actions
    useEffect(() => {
        const handleKeyDown = (e: KeyboardEvent) => {
            // Get the target card (selected or hovered)
            const targetCard = selectedCard || hoveredCard;
            if (!targetCard) return;

            // Prevent shortcuts when typing in inputs
            if (e.target instanceof HTMLInputElement || e.target instanceof HTMLTextAreaElement) return;

            const key = e.key.toLowerCase();

            // Hand card shortcuts
            if (targetCard.zone === 'hand') {
                switch (key) {
                    case 'b': // Move to Battlefield
                        playCard(targetCard);
                        setSelectedCard(null);
                        break;
                    case 'g': // Move to Graveyard (discard)
                        setHand(prev => prev.filter(c => c.id !== targetCard.id));
                        setGraveyard(prev => [...prev, { ...targetCard, zone: 'graveyard' as Zone }]);
                        setSelectedCard(null);
                        break;
                    case 'e': // Move to Exile
                        setHand(prev => prev.filter(c => c.id !== targetCard.id));
                        setExile(prev => [...prev, { ...targetCard, zone: 'exile' as Zone }]);
                        setSelectedCard(null);
                        break;
                    case 'l': // Move to Top of Library
                        if (e.shiftKey) {
                            // Shift+L = Bottom of library
                            setHand(prev => prev.filter(c => c.id !== targetCard.id));
                            setLibrary(prev => [...prev, { ...targetCard, zone: 'library' as Zone }]);
                        } else {
                            // L = Top of library
                            setHand(prev => prev.filter(c => c.id !== targetCard.id));
                            setLibrary(prev => [{ ...targetCard, zone: 'library' as Zone }, ...prev]);
                        }
                        setSelectedCard(null);
                        break;
                }
            }

            // Battlefield card shortcuts
            if (targetCard.zone === 'battlefield') {
                switch (key) {
                    case 't': // Tap/Untap
                        toggleTap(targetCard);
                        break;
                    case 'g': // Move to Graveyard
                        toGraveyard(targetCard);
                        setSelectedCard(null);
                        break;
                    case 'e': // Move to Exile
                        toExile(targetCard);
                        setSelectedCard(null);
                        break;
                    case 'h': // Return to Hand
                        toHand(targetCard);
                        setSelectedCard(null);
                        break;
                    case 'l': // Put on Top of Library
                        setBattlefield(prev => prev.filter(c => c.id !== targetCard.id));
                        setLibrary(prev => [{ ...targetCard, zone: 'library' as Zone }, ...prev]);
                        setSelectedCard(null);
                        break;
                }
            }

            // Global shortcuts
            switch (key) {
                case 'd': // Draw a card
                    if (!e.ctrlKey && !e.metaKey) {
                        drawCard(1);
                    }
                    break;
                case 'n': // Next turn
                    nextTurn();
                    break;
                case 'escape': // Deselect card
                    setSelectedCard(null);
                    setContextMenuCard(null);
                    break;
            }
        };

        window.addEventListener('keydown', handleKeyDown);
        return () => window.removeEventListener('keydown', handleKeyDown);
    }, [selectedCard, hoveredCard, playCard, toggleTap, toGraveyard, toExile, toHand, drawCard, nextTurn]);

    // Card rendering
    const renderCard = (cardInstance: CardInstance, size: 'sm' | 'md' | 'lg' = 'md', showActions = true) => {
        const { card, tapped, faceDown } = cardInstance;
        const sizeClasses = {
            sm: 'w-12 h-16',
            md: 'w-24 h-32',
            lg: 'w-32 h-44',
        };

        // Castable card highlighting: green glow for castable spells in hand, amber for lands
        const isInHand = cardInstance.zone === 'hand';
        const isCastable = isInHand && !card.isLand && gameStarted && card.cmc <= stats.effectiveManaAvailable;
        const isPlayableLand = isInHand && card.isLand && gameStarted;
        const castableClass = isCastable
            ? 'ring-2 ring-green-400/70 shadow-[0_0_8px_rgba(74,222,128,0.4)]'
            : isPlayableLand
                ? 'ring-1 ring-amber-400/50 shadow-[0_0_6px_rgba(251,191,36,0.25)]'
                : '';

        return (
            <div
                key={cardInstance.id}
                className={`${sizeClasses[size]} relative rounded-lg overflow-hidden cursor-pointer transition-all duration-200 hover:scale-105 hover:z-10 ${tapped ? 'rotate-90' : ''} ${selectedCard?.id === cardInstance.id ? 'ring-2 ring-cyan-400' : castableClass}`}
                onClick={() => {
                    if (showActions) {
                        setSelectedCard(selectedCard?.id === cardInstance.id ? null : cardInstance);
                    }
                }}
                onMouseEnter={() => setHoveredCard(cardInstance)}
                onMouseLeave={() => setHoveredCard(null)}
                onDoubleClick={() => {
                    if (cardInstance.zone === 'hand') playCard(cardInstance);
                    else if (cardInstance.zone === 'battlefield') toggleTap(cardInstance);
                }}
                onContextMenu={(e) => {
                    e.preventDefault();
                    // Context menu for battlefield AND hand cards
                    if (cardInstance.zone === 'battlefield' || cardInstance.zone === 'hand') {
                        setContextMenuCard({ card: cardInstance, x: e.clientX, y: e.clientY });
                    }
                }}
                draggable={cardInstance.zone === 'battlefield' || cardInstance.zone === 'hand'}
                onDragStart={() => setDraggedCard(cardInstance)}
                onDragEnd={() => setDraggedCard(null)}
            >
                {faceDown ? (
                    <div
                        className="w-full h-full flex items-center justify-center"
                        style={{ background: getSleeveGradient(sleeveColors) }}
                    >
                        <span className="text-2xl opacity-60">🂠</span>
                    </div>
                ) : card.imageUrl ? (
                    <img
                        src={cardInstance.transformed && card.backImageUrl ? card.backImageUrl : card.imageUrl}
                        alt={card.name}
                        className="w-full h-full object-cover"
                        loading="lazy"
                    />
                ) : (
                    <div className={`w-full h-full flex flex-col items-center justify-center p-1 text-center ${card.isLand ? 'bg-gradient-to-br from-amber-800 to-amber-900' : 'bg-gradient-to-br from-slate-700 to-slate-800'}`}>
                        <span className="text-[8px] font-bold text-white leading-tight line-clamp-3">{card.name}</span>
                        {!card.isLand && (
                            <span className="text-[10px] text-cyan-400 mt-1">{card.cmc}</span>
                        )}
                    </div>
                )}

                {/* CMC badge for non-lands */}
                {!card.isLand && size !== 'sm' && (
                    <div className="absolute top-0.5 right-0.5 w-5 h-5 rounded-full bg-black/70 flex items-center justify-center">
                        <span className="text-xs font-bold text-white">{card.cmc}</span>
                    </div>
                )}

                {/* Token/Copy indicator */}
                {(cardInstance.isToken || cardInstance.isCopy) && (
                    <div className={`absolute top-0.5 left-0.5 px-1 py-0.5 rounded text-[8px] font-bold ${cardInstance.isToken ? 'bg-amber-500/80 text-black' : 'bg-cyan-500/80 text-black'}`}>
                        {cardInstance.isToken ? 'T' : 'C'}
                    </div>
                )}

                {/* Counter badges */}
                {cardInstance.counters && Object.keys(cardInstance.counters).length > 0 && size !== 'sm' && (
                    <div className="absolute bottom-0.5 left-0.5 right-0.5 flex flex-wrap gap-0.5 justify-center">
                        {Object.entries(cardInstance.counters).map(([type, count]) => (
                            <div
                                key={type}
                                className={`px-1 py-0.5 rounded text-[8px] font-bold ${type === '+1/+1' ? 'bg-green-500 text-black' :
                                    type === '-1/-1' ? 'bg-red-500 text-white' :
                                        type === 'loyalty' ? 'bg-blue-500 text-white' :
                                            'bg-purple-500 text-white'
                                    }`}
                                title={`${count} ${type} counter${count !== 1 ? 's' : ''}`}
                            >
                                {count}{type === '+1/+1' ? '↑' : type === '-1/-1' ? '↓' : type === 'loyalty' ? '♦' : '●'}
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    };

    // Card actions menu
    const renderCardActions = () => {
        if (!selectedCard) return null;

        return (
            <div className="fixed bottom-4 left-1/2 -translate-x-1/2 bg-slate-800/95 backdrop-blur rounded-xl border border-white/20 p-3 flex items-center gap-2 z-50 shadow-2xl">
                <span className="text-sm text-white/70 mr-2">{selectedCard.card.name}</span>

                {selectedCard.zone === 'hand' && (
                    <button
                        onClick={() => { playCard(selectedCard); setSelectedCard(null); }}
                        className="px-3 py-1.5 bg-green-600 hover:bg-green-500 text-white text-sm rounded-lg transition-colors"
                    >
                        Play
                    </button>
                )}

                {selectedCard.zone === 'battlefield' && (
                    <button
                        onClick={() => { toggleTap(selectedCard); setSelectedCard(null); }}
                        className="px-3 py-1.5 bg-blue-600 hover:bg-blue-500 text-white text-sm rounded-lg transition-colors"
                    >
                        {selectedCard.tapped ? 'Untap' : 'Tap'}
                    </button>
                )}

                {selectedCard.zone !== 'graveyard' && (
                    <button
                        onClick={() => { toGraveyard(selectedCard); setSelectedCard(null); }}
                        className="px-3 py-1.5 bg-red-600/80 hover:bg-red-500 text-white text-sm rounded-lg transition-colors"
                    >
                        → Graveyard
                    </button>
                )}

                {selectedCard.zone !== 'exile' && (
                    <button
                        onClick={() => { toExile(selectedCard); setSelectedCard(null); }}
                        className="px-3 py-1.5 bg-purple-600/80 hover:bg-purple-500 text-white text-sm rounded-lg transition-colors"
                    >
                        → Exile
                    </button>
                )}

                {selectedCard.zone !== 'hand' && (
                    <button
                        onClick={() => { toHand(selectedCard); setSelectedCard(null); }}
                        className="px-3 py-1.5 bg-amber-600/80 hover:bg-amber-500 text-white text-sm rounded-lg transition-colors"
                    >
                        → Hand
                    </button>
                )}

                <button
                    onClick={() => setSelectedCard(null)}
                    className="px-2 py-1.5 bg-white/10 hover:bg-white/20 text-white text-sm rounded-lg transition-colors ml-2"
                >
                    <X className="size-4" />
                </button>
            </div>
        );
    };

    // Right-click context menu for battlefield cards
    const renderContextMenu = () => {
        if (!contextMenuCard) return null;

        const { card: cardInstance, x, y } = contextMenuCard;
        const isInHand = cardInstance.zone === 'hand';
        const isOnBattlefield = cardInstance.zone === 'battlefield';

        return (
            <>
                {/* Backdrop to close menu */}
                <div
                    className="fixed inset-0 z-[60]"
                    onClick={() => setContextMenuCard(null)}
                />

                {/* Context menu */}
                <div
                    className="fixed z-[70] bg-slate-800/95 backdrop-blur rounded-lg border border-white/20 p-2 shadow-2xl min-w-[200px]"
                    style={{ left: Math.min(x, window.innerWidth - 220), top: Math.min(y, window.innerHeight - 400) }}
                >
                    <div className="text-xs text-white/50 px-2 py-1 border-b border-white/10 mb-1 truncate max-w-[180px]">
                        {cardInstance.card.name}
                    </div>

                    <div className="space-y-0.5">
                        {/* Hand card options */}
                        {isInHand && (
                            <>
                                <button onClick={() => { playCard(cardInstance); setContextMenuCard(null); }} className="w-full px-2 py-1.5 bg-green-600/30 hover:bg-green-500/50 text-green-300 text-xs rounded text-left transition-colors flex justify-between">
                                    <span>Move to Battlefield</span><span className="text-white/30">B</span>
                                </button>
                                <button onClick={() => { playCard(cardInstance, { tapped: true }); setContextMenuCard(null); }} className="w-full px-2 py-1.5 bg-white/5 hover:bg-white/15 text-white text-xs rounded text-left transition-colors">
                                    Move to Battlefield Tapped
                                </button>
                                <div className="border-t border-white/10 my-1" />
                                <button onClick={() => {
                                    setHand(prev => prev.filter(c => c.id !== cardInstance.id));
                                    setLibrary(prev => [cardInstance, ...prev]);
                                    setContextMenuCard(null);
                                }} className="w-full px-2 py-1.5 bg-white/5 hover:bg-white/15 text-white text-xs rounded text-left transition-colors flex justify-between">
                                    <span>Move to Top of Library</span><span className="text-white/30">L</span>
                                </button>
                                <button onClick={() => {
                                    setHand(prev => prev.filter(c => c.id !== cardInstance.id));
                                    setLibrary(prev => [...prev, cardInstance]);
                                    setContextMenuCard(null);
                                }} className="w-full px-2 py-1.5 bg-white/5 hover:bg-white/15 text-white text-xs rounded text-left transition-colors">
                                    Move to Bottom of Library
                                </button>
                                <div className="border-t border-white/10 my-1" />
                                <button onClick={() => {
                                    setHand(prev => prev.filter(c => c.id !== cardInstance.id));
                                    setGraveyard(prev => [...prev, { ...cardInstance, zone: 'graveyard' }]);
                                    setContextMenuCard(null);
                                }} className="w-full px-2 py-1.5 bg-red-600/30 hover:bg-red-500/50 text-red-300 text-xs rounded text-left transition-colors flex justify-between">
                                    <span>Move to Graveyard</span><span className="text-white/30">G</span>
                                </button>
                                <button onClick={() => {
                                    setHand(prev => prev.filter(c => c.id !== cardInstance.id));
                                    setExile(prev => [...prev, { ...cardInstance, zone: 'exile' }]);
                                    setContextMenuCard(null);
                                }} className="w-full px-2 py-1.5 bg-purple-600/30 hover:bg-purple-500/50 text-purple-300 text-xs rounded text-left transition-colors flex justify-between">
                                    <span>Move to Exile</span><span className="text-white/30">E</span>
                                </button>
                            </>
                        )}

                        {/* Battlefield card options */}
                        {isOnBattlefield && (
                            <>
                                <button onClick={() => { toggleTap(cardInstance); setContextMenuCard(null); }} className="w-full px-2 py-1.5 bg-white/5 hover:bg-white/15 text-white text-xs rounded text-left transition-colors flex justify-between">
                                    <span>{cardInstance.tapped ? '↺ Untap' : '↻ Tap'}</span><span className="text-white/30">T</span>
                                </button>

                                {cardInstance.card.backImageUrl && (
                                    <button
                                        onClick={() => {
                                            setBattlefield(prev => prev.map(c =>
                                                c.id === cardInstance.id ? { ...c, transformed: !c.transformed } : c
                                            ));
                                            setContextMenuCard(null);
                                        }}
                                        className="w-full px-2 py-1.5 bg-white/5 hover:bg-white/15 text-white text-xs rounded text-left transition-colors"
                                    >
                                        🔃 Transform
                                    </button>
                                )}

                                <div className="border-t border-white/10 my-1" />
                                <div className="text-[9px] text-white/40 px-2 py-0.5">COUNTERS</div>
                                <div className="grid grid-cols-2 gap-0.5 px-1">
                                    <button onClick={() => { modifyCounter(cardInstance.id, '+1/+1', 1); setContextMenuCard(null); }} className="px-2 py-1 bg-green-600/40 hover:bg-green-500 text-white text-[10px] rounded transition-colors">+1/+1 ↑</button>
                                    <button onClick={() => { modifyCounter(cardInstance.id, '+1/+1', -1); setContextMenuCard(null); }} className="px-2 py-1 bg-green-800/40 hover:bg-green-700 text-white text-[10px] rounded transition-colors">+1/+1 ↓</button>
                                    <button onClick={() => { modifyCounter(cardInstance.id, 'loyalty', 1); setContextMenuCard(null); }} className="px-2 py-1 bg-blue-600/40 hover:bg-blue-500 text-white text-[10px] rounded transition-colors">Loyalty +</button>
                                    <button onClick={() => { modifyCounter(cardInstance.id, 'loyalty', -1); setContextMenuCard(null); }} className="px-2 py-1 bg-blue-800/40 hover:bg-blue-700 text-white text-[10px] rounded transition-colors">Loyalty -</button>
                                </div>

                                <div className="border-t border-white/10 my-1" />
                                <button onClick={() => { createToken(cardInstance); setContextMenuCard(null); }} className="w-full px-2 py-1.5 bg-amber-600/30 hover:bg-amber-500/50 text-amber-300 text-xs rounded text-left transition-colors">
                                    📋 Create Token Copy
                                </button>
                                <button onClick={() => { createCopy(cardInstance); setContextMenuCard(null); }} className="w-full px-2 py-1.5 bg-cyan-600/30 hover:bg-cyan-500/50 text-cyan-300 text-xs rounded text-left transition-colors">
                                    🔄 Clone (Copy)
                                </button>

                                <div className="border-t border-white/10 my-1" />
                                <button onClick={() => { toHand(cardInstance); setContextMenuCard(null); }} className="w-full px-2 py-1.5 bg-blue-600/30 hover:bg-blue-500/50 text-blue-300 text-xs rounded text-left transition-colors">
                                    🖐 Return to Hand
                                </button>
                                <button onClick={() => {
                                    setBattlefield(prev => prev.filter(c => c.id !== cardInstance.id));
                                    setLibrary(prev => [{ ...cardInstance, zone: 'library' }, ...prev]);
                                    setContextMenuCard(null);
                                }} className="w-full px-2 py-1.5 bg-white/5 hover:bg-white/15 text-white text-xs rounded text-left transition-colors">
                                    📚 Put on Top of Library
                                </button>
                                <button onClick={() => { toGraveyard(cardInstance); setContextMenuCard(null); }} className="w-full px-2 py-1.5 bg-red-600/30 hover:bg-red-500/50 text-red-300 text-xs rounded text-left transition-colors flex justify-between">
                                    <span>💀 Move to Graveyard</span><span className="text-white/30">G</span>
                                </button>
                                <button onClick={() => { toExile(cardInstance); setContextMenuCard(null); }} className="w-full px-2 py-1.5 bg-purple-600/30 hover:bg-purple-500/50 text-purple-300 text-xs rounded text-left transition-colors flex justify-between">
                                    <span>✨ Move to Exile</span><span className="text-white/30">E</span>
                                </button>
                            </>
                        )}
                    </div>
                </div>
            </>
        );
    };

    return (
        <div className="fixed inset-0 bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 z-50 flex flex-col overflow-hidden">
            {/* Header */}
            <div className="flex items-center justify-between px-4 py-2 bg-black/30 border-b border-white/10">
                <div className="flex items-center gap-4">
                    <h2 className="text-lg font-bold text-white flex items-center gap-2">
                        <Sparkles className="size-5 text-cyan-400" />
                        Playtester: {deckName}
                    </h2>

                    {/* Mode indicator */}
                    <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${mode === 'session'
                        ? 'bg-red-500/20 text-red-400 border border-red-500/30'
                        : 'bg-green-500/20 text-green-400 border border-green-500/30'
                        }`}>
                        {mode === 'session' ? '🔴 LIVE SESSION' : '🐟 Goldfish'}
                    </span>
                    {mode === 'session' && (
                        <span className="text-[10px] text-white/40" title="Your hand is visible to you but hidden from the broadcast overlay">
                            Hand: Private
                        </span>
                    )}

                    {/* Plan Selector Dropdown - Always visible */}
                    <div className="relative">
                        <select
                            value={selectedPlan}
                            onChange={(e) => {
                                setSelectedPlan(e.target.value as PlanId);
                                // Reset game when plan changes
                                if (gameStarted) {
                                    startNewGame();
                                }
                            }}
                            className="appearance-none bg-slate-700/80 border border-cyan-500/30 rounded-lg px-3 py-1.5 pr-8 text-sm text-white cursor-pointer hover:bg-slate-600/80 transition-colors"
                            title="Select deck configuration"
                        >
                            <option value="base">📋 Base Deck</option>
                            <option value="A">🎯 Match 1</option>
                            <option value="B">🎯 Match 2</option>
                            <option value="C">🎯 Match 3</option>
                            <option value="D">🎯 Match 4</option>
                            <option value="E">🎯 Match 5</option>
                        </select>
                        <ChevronDown className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none text-white/50 size-4" />
                    </div>

                    {/* Show if plan has modifications */}
                    {selectedPlan !== 'base' && plans && Object.values(plans).some(p => {
                        const planKey = selectedPlan as 'A' | 'B' | 'C' | 'D' | 'E';
                        return p[planKey] !== undefined || (p.mvs && p.mvs[planKey] !== undefined);
                    }) && (
                            <span className="text-[10px] text-amber-400" title="This plan has sideboard/MV modifications">
                                ✦ Modified
                            </span>
                        )}

                    {gameStarted && (
                        <div className="flex items-center gap-3 text-sm">
                            <span className="text-white/60">Turn</span>
                            <span className="text-xl font-bold text-cyan-400">{turn}</span>

                            <span className="text-white/30">|</span>

                            <span className="text-white/60">Library</span>
                            <span className="text-white font-medium">{library.length}</span>
                        </div>
                    )}
                </div>

                <div className="flex items-center gap-2">
                    {/* Zoom controls */}
                    <div className="flex items-center gap-1 bg-white/5 rounded-lg px-2 py-1">
                        <button
                            onClick={() => setBattlefieldZoom(z => Math.max(0.5, z - 0.1))}
                            className="text-white/60 hover:text-white text-sm px-1"
                            title="Zoom Out"
                        >
                            −
                        </button>
                        <input
                            type="range"
                            min="0.5"
                            max="2"
                            step="0.1"
                            value={battlefieldZoom}
                            onChange={(e) => setBattlefieldZoom(parseFloat(e.target.value))}
                            className="w-16 h-1 accent-cyan-400"
                            title={`Zoom: ${Math.round(battlefieldZoom * 100)}%`}
                        />
                        <button
                            onClick={() => setBattlefieldZoom(z => Math.min(2, z + 0.1))}
                            className="text-white/60 hover:text-white text-sm px-1"
                            title="Zoom In"
                        >
                            +
                        </button>
                        <span className="text-[10px] text-white/40 w-8">{Math.round(battlefieldZoom * 100)}%</span>
                    </div>

                    <button
                        onClick={() => setShowStats(!showStats)}
                        className={`p-2 rounded-lg transition-colors ${showStats ? 'bg-cyan-500/20 text-cyan-400' : 'bg-white/10 text-white/60'}`}
                        title="Toggle Stats"
                    >
                        <TrendingUp className="size-4" />
                    </button>

                    <button
                        onClick={() => setShowLibraryTop(!showLibraryTop)}
                        className={`p-2 rounded-lg transition-colors ${showLibraryTop ? 'bg-amber-500/20 text-amber-400' : 'bg-white/10 text-white/60'}`}
                        title="Peek Top Card"
                    >
                        {showLibraryTop ? <Eye className="size-4" /> : <EyeOff className="size-4" />}
                    </button>

                    <button
                        onClick={startNewGame}
                        className="p-2 bg-white/10 hover:bg-white/20 text-white/80 rounded-lg transition-colors"
                        title="New Game"
                    >
                        <RotateCcw className="size-4" />
                    </button>

                    <button
                        onClick={() => {
                            if (mode === 'session') endSession();
                            onClose();
                        }}
                        className="p-2 bg-red-500/20 hover:bg-red-500/30 text-red-400 rounded-lg transition-colors"
                    >
                        <X className="size-5" />
                    </button>
                </div>
            </div>

            {/* Main content */}
            <div className="flex-1 flex overflow-hidden">
                {/* Game area */}
                <div className="flex-1 flex flex-col p-4 gap-4 overflow-auto">
                    {/* Pre-game: Plan selection and draw opening hand */}
                    {!gameStarted && (
                        <div className="flex-1 flex items-center justify-center">
                            <div className="bg-slate-800/80 backdrop-blur rounded-2xl border border-white/20 p-8 text-center max-w-md">
                                <h3 className="text-2xl font-bold text-white mb-2">Ready to Goldfish?</h3>

                                {/* Plan Selection */}
                                {plans && Object.keys(plans).length > 0 && (
                                    <div className="mb-6">
                                        <p className="text-white/50 text-sm mb-3">Select Match Configuration:</p>
                                        <div className="flex flex-wrap justify-center gap-2">
                                            {(['base', 'A', 'B', 'C', 'D', 'E'] as PlanId[]).map(plan => {
                                                // Check if any card has this plan (quantity OR MV override)
                                                const planKey = plan as 'A' | 'B' | 'C' | 'D' | 'E';
                                                const hasPlan = plan === 'base' || Object.values(plans).some(p =>
                                                    p[planKey] !== undefined || (p.mvs && p.mvs[planKey] !== undefined)
                                                );
                                                if (!hasPlan) return null;

                                                return (
                                                    <button
                                                        key={plan}
                                                        onClick={() => setSelectedPlan(plan)}
                                                        className={`px-4 py-2 rounded-lg font-medium transition-all ${selectedPlan === plan
                                                            ? 'bg-cyan-500 text-white shadow-lg shadow-cyan-500/30'
                                                            : 'bg-white/10 text-white/60 hover:bg-white/20'
                                                            }`}
                                                    >
                                                        <Target className="size-4 inline mr-1" />
                                                        {plan === 'base' ? 'Base' : `Match ${plan === 'A' ? '1' : plan === 'B' ? '2' : plan === 'C' ? '3' : plan === 'D' ? '4' : '5'}`}
                                                    </button>
                                                );
                                            })}
                                        </div>
                                        <p className="text-white/30 text-xs mt-2">
                                            {selectedPlan === 'base'
                                                ? `${effectiveDeck.reduce((s, c) => s + c.quantity, 0)} cards (base deck)`
                                                : `${effectiveDeck.reduce((s, c) => s + c.quantity, 0)} cards (with modifications)`
                                            }
                                        </p>
                                    </div>
                                )}

                                <p className="text-white/60 mb-6">
                                    {mulliganCount === 0
                                        ? 'Draw your opening 7 cards'
                                        : `Draw 7, then put ${mulliganCount} card${mulliganCount > 1 ? 's' : ''} on bottom`
                                    }
                                </p>

                                <div className="flex items-center justify-center gap-4">
                                    <button
                                        onClick={drawOpeningHand}
                                        className="px-6 py-3 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white font-semibold rounded-xl transition-all shadow-lg"
                                    >
                                        <Hand className="size-5 inline mr-2" />
                                        Draw 7
                                    </button>

                                    {mulliganCount > 0 && (
                                        <span className="text-amber-400 text-sm">
                                            Mulligan #{mulliganCount}
                                        </span>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}

                    {/* London Mulligan: Decide Keep or Mull */}
                    {mulliganPhase === 'decide' && (
                        <div className="flex-1 flex flex-col items-center justify-center p-4">
                            <div className="bg-slate-800/80 backdrop-blur rounded-2xl border border-cyan-500/30 p-6 text-center max-w-2xl">
                                <h3 className="text-xl font-bold text-cyan-400 mb-2">
                                    {mulliganCount === 0 ? 'Opening Hand' : `Mulligan ${mulliganCount}`}
                                </h3>
                                <p className="text-white/60 mb-4">
                                    {mulliganCount === 0
                                        ? 'Here is your opening hand. Keep or mulligan?'
                                        : `You'll put ${mulliganCount} card${mulliganCount > 1 ? 's' : ''} on bottom if you keep.`
                                    }
                                </p>

                                {/* Hand preview */}
                                <div className="flex flex-wrap gap-2 justify-center mb-6">
                                    {hand.map(cardInstance => (
                                        <div key={cardInstance.id} className="w-20 h-28 rounded-lg overflow-hidden shadow-lg">
                                            {cardInstance.card.imageUrl ? (
                                                <img src={cardInstance.card.imageUrl} alt={cardInstance.card.name} className="w-full h-full object-cover" />
                                            ) : (
                                                <div className="w-full h-full bg-slate-700 flex items-center justify-center p-1 text-center">
                                                    <span className="text-[9px] text-white font-bold">{cardInstance.card.name}</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center justify-center gap-4">
                                    <button
                                        onClick={keepHand}
                                        className="px-8 py-3 bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white font-semibold rounded-xl transition-all shadow-lg"
                                    >
                                        ✓ Keep
                                    </button>

                                    <button
                                        onClick={mulligan}
                                        disabled={mulliganCount >= 6}
                                        className="px-8 py-3 bg-amber-600/80 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-xl transition-all"
                                    >
                                        ↺ Mulligan
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* London Mulligan: Choose cards to put on bottom */}
                    {mulliganPhase === 'choose' && (
                        <div className="flex-1 flex flex-col items-center justify-center p-4">
                            <div className="bg-slate-800/80 backdrop-blur rounded-2xl border border-amber-500/30 p-6 text-center max-w-2xl">
                                <h3 className="text-xl font-bold text-amber-400 mb-2">
                                    London Mulligan
                                </h3>
                                <p className="text-white/60 mb-4">
                                    Select {mulliganCount} card{mulliganCount > 1 ? 's' : ''} to put on the bottom of your library.
                                    <br />
                                    <span className="text-amber-400 font-medium">
                                        {cardsToBottom.length} / {mulliganCount} selected
                                    </span>
                                </p>

                                {/* Hand display for selection */}
                                <div className="flex flex-wrap gap-2 justify-center mb-6">
                                    {hand.map(cardInstance => (
                                        <div
                                            key={cardInstance.id}
                                            className={`w-24 h-32 relative rounded-lg overflow-hidden cursor-pointer transition-all duration-200 ${cardsToBottom.includes(cardInstance.id)
                                                ? 'ring-2 ring-red-500 opacity-50 scale-95'
                                                : 'hover:scale-105'
                                                }`}
                                            onClick={() => toggleCardForBottom(cardInstance.id)}
                                        >
                                            {cardInstance.card.imageUrl ? (
                                                <img
                                                    src={cardInstance.card.imageUrl}
                                                    alt={cardInstance.card.name}
                                                    className="w-full h-full object-cover"
                                                />
                                            ) : (
                                                <div className="w-full h-full bg-slate-700 flex items-center justify-center p-1 text-center">
                                                    <span className="text-[10px] text-white font-bold">{cardInstance.card.name}</span>
                                                </div>
                                            )}

                                            {/* Selection indicator */}
                                            {cardsToBottom.includes(cardInstance.id) && (
                                                <div className="absolute inset-0 bg-red-500/30 flex items-center justify-center">
                                                    <span className="text-2xl">⬇️</span>
                                                </div>
                                            )}
                                        </div>
                                    ))}
                                </div>

                                <div className="flex items-center justify-center gap-4">
                                    <button
                                        onClick={confirmMulligan}
                                        disabled={cardsToBottom.length !== mulliganCount}
                                        className={`px-6 py-3 font-semibold rounded-xl transition-all shadow-lg ${cardsToBottom.length === mulliganCount
                                            ? 'bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-500 hover:to-emerald-500 text-white'
                                            : 'bg-slate-600 text-white/50 cursor-not-allowed'
                                            }`}
                                    >
                                        Confirm & Keep
                                    </button>

                                    <button
                                        onClick={mulligan}
                                        disabled={mulliganCount >= 6}
                                        className="px-6 py-3 bg-amber-600/80 hover:bg-amber-500 disabled:opacity-50 text-white font-semibold rounded-xl transition-all"
                                    >
                                        Mulligan Again
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Game in progress - Moxfield-style layout */}
                    {gameStarted && (
                        <div className="flex-1 flex flex-col overflow-hidden">
                            {/* Main row: Battlefield + Action Column */}
                            <div className="flex-1 flex gap-2 min-h-0">
                                {/* Battlefield - Takes most space */}
                                <div
                                    className="flex-1 bg-gradient-to-b from-slate-800/40 to-slate-900/60 rounded-xl border border-white/10 p-3 flex flex-col overflow-hidden"
                                    onDragOver={(e) => e.preventDefault()}
                                    onDrop={(e) => {
                                        if (draggedCard) {
                                            const rect = e.currentTarget.getBoundingClientRect();
                                            updateCardPosition(draggedCard.id, e.clientX - rect.left, e.clientY - rect.top);
                                        }
                                    }}
                                >
                                    {/* Non-land permanents area (fills available space) */}
                                    <div className="flex-1 overflow-y-auto">
                                        <div
                                            className="flex flex-wrap gap-2 content-start"
                                            style={{ transform: `scale(${battlefieldZoom})`, transformOrigin: 'top left' }}
                                        >
                                            {battlefield.filter(c => !c.card.isLand).map(card => renderCard(card, 'md'))}
                                        </div>
                                        {battlefield.filter(c => !c.card.isLand).length === 0 && (
                                            <div className="h-full flex items-center justify-center">
                                                <span className="text-white/10 text-lg">Battlefield</span>
                                            </div>
                                        )}
                                    </div>

                                    {/* Lands row at bottom of battlefield */}
                                    <div className="border-t border-white/5 pt-2 mt-2">
                                        <div
                                            className="flex flex-wrap gap-1.5"
                                            style={{ transform: `scale(${battlefieldZoom})`, transformOrigin: 'top left' }}
                                        >
                                            {battlefield.filter(c => c.card.isLand).map(card => renderCard(card, 'sm'))}
                                            {battlefield.filter(c => c.card.isLand).length === 0 && (
                                                <span className="text-white/20 text-xs italic">No lands</span>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Action Column (right side) - Moxfield style */}
                                <div className="w-28 flex flex-col gap-1">
                                    <button
                                        onClick={() => drawCard(1)}
                                        disabled={library.length === 0}
                                        className="w-full px-2 py-2 bg-blue-600 hover:bg-blue-500 disabled:opacity-50 text-white text-xs font-medium rounded transition-colors"
                                    >
                                        Draw
                                    </button>
                                    <button
                                        onClick={() => setLibrary(shuffleArray(library))}
                                        className="w-full px-2 py-2 bg-amber-600 hover:bg-amber-500 text-white text-xs font-medium rounded transition-colors"
                                    >
                                        Shuffle
                                    </button>
                                    <button
                                        onClick={() => setShowLibraryTop(!showLibraryTop)}
                                        className="w-full px-2 py-2 bg-slate-600 hover:bg-slate-500 text-white text-xs font-medium rounded transition-colors"
                                    >
                                        {showLibraryTop ? 'Hide Top' : 'View Library'}
                                    </button>
                                    <button
                                        onClick={nextTurn}
                                        className="w-full px-2 py-2 bg-gradient-to-r from-cyan-600 to-blue-600 hover:from-cyan-500 hover:to-blue-500 text-white text-xs font-medium rounded transition-colors"
                                    >
                                        Next Turn
                                    </button>
                                    <button
                                        onClick={startNewGame}
                                        className="w-full px-2 py-2 bg-slate-700 hover:bg-slate-600 text-white text-xs font-medium rounded transition-colors"
                                    >
                                        Restart
                                    </button>

                                    {/* Show stats toggle */}
                                    <button
                                        onClick={() => setShowStats(!showStats)}
                                        className={`w-full px-2 py-2 text-xs font-medium rounded transition-colors ${showStats ? 'bg-cyan-600 text-white' : 'bg-slate-800 text-white/50 hover:bg-slate-700'}`}
                                    >
                                        {showStats ? 'Hide Stats' : 'Show Stats'}
                                    </button>
                                </div>
                            </div>

                            {/* Bottom bar: Hand + Zones (Moxfield style) */}
                            <div className="flex gap-2 mt-1 shrink-0">
                                {/* Hand section */}
                                <div className="flex-1 bg-slate-900/80 rounded-lg border border-white/10 p-2">
                                    <div className="flex items-center justify-between mb-1">
                                        <span className="text-[10px] text-white/70 uppercase tracking-wide flex items-center gap-1">
                                            <Hand className="size-3" /> Hand ({hand.length})
                                        </span>
                                        <span className="text-[9px] text-white/30">Double-click to play</span>
                                    </div>

                                    <div className="flex gap-1.5 overflow-x-auto">
                                        {hand.map((card, index) => (
                                            <div
                                                key={card.id}
                                                draggable
                                                onDragStart={() => setDragHandIndex(index)}
                                                onDragOver={(e) => e.preventDefault()}
                                                onDrop={() => {
                                                    if (dragHandIndex !== null && dragHandIndex !== index) {
                                                        const newHand = [...hand];
                                                        const [moved] = newHand.splice(dragHandIndex, 1);
                                                        newHand.splice(index, 0, moved);
                                                        setHand(newHand);
                                                    }
                                                    setDragHandIndex(null);
                                                }}
                                                onDragEnd={() => setDragHandIndex(null)}
                                                className="flex-shrink-0 transition-transform hover:-translate-y-1"
                                            >
                                                {renderCard(card, 'sm')}
                                            </div>
                                        ))}
                                        {hand.length === 0 && (
                                            <div className="text-white/30 text-xs italic py-2">Empty hand</div>
                                        )}
                                    </div>
                                </div>

                                {/* Zones panel (Library, Graveyard, Exile) */}
                                <div className="flex gap-1.5 shrink-0">
                                    {/* Library */}
                                    <div
                                        className="w-16 bg-slate-800/60 rounded-lg border border-slate-600/30 p-1.5 flex flex-col items-center cursor-pointer hover:bg-slate-700/60 transition-colors"
                                        onClick={() => setShowLibraryTop(!showLibraryTop)}
                                    >
                                        <div className="text-[8px] text-slate-400/70 uppercase mb-0.5">Library</div>
                                        <div
                                            className="w-10 h-14 rounded border border-slate-600/50 flex items-center justify-center shadow-lg"
                                            style={{ background: getSleeveGradient(sleeveColors) }}
                                        >
                                            {showLibraryTop && library.length > 0 && library[0].card.imageUrl ? (
                                                <img src={library[0].card.imageUrl} alt="" className="w-full h-full object-cover rounded" />
                                            ) : (
                                                <span className="text-white/60 text-xs font-bold">{library.length}</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Graveyard */}
                                    <div className="w-16 bg-slate-800/60 rounded-lg border border-red-500/20 p-1.5 flex flex-col items-center">
                                        <div className="text-[8px] text-red-400/70 uppercase mb-0.5">Graveyard</div>
                                        <div className="w-10 h-14 rounded border border-red-500/20 bg-slate-900/50 flex items-center justify-center overflow-hidden">
                                            {graveyard.length > 0 && graveyard[graveyard.length - 1].card.imageUrl ? (
                                                <img src={graveyard[graveyard.length - 1].card.imageUrl} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="text-white/40 text-xs">{graveyard.length}</span>
                                            )}
                                        </div>
                                    </div>

                                    {/* Exile */}
                                    <div className="w-16 bg-slate-800/60 rounded-lg border border-purple-500/20 p-1.5 flex flex-col items-center">
                                        <div className="text-[8px] text-purple-400/70 uppercase mb-0.5">Exile</div>
                                        <div className="w-10 h-14 rounded border border-purple-500/20 bg-slate-900/50 flex items-center justify-center overflow-hidden">
                                            {exile.length > 0 && exile[exile.length - 1].card.imageUrl ? (
                                                <img src={exile[exile.length - 1].card.imageUrl} alt="" className="w-full h-full object-cover" />
                                            ) : (
                                                <span className="text-white/40 text-xs">{exile.length}</span>
                                            )}
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div >

                {/* Stats sidebar */}
                {
                    showStats && gameStarted && (
                        <div className="w-64 bg-black/30 border-l border-white/10 p-4 overflow-auto">
                            <h3 className="text-sm font-semibold text-white/80 mb-4 flex items-center gap-2">
                                <TrendingUp className="size-4 text-cyan-400" />
                                Game Stats
                            </h3>

                            <div className="space-y-4">
                                {/* Mana available (EMA) */}
                                <div className="bg-white/5 rounded-lg p-3">
                                    <div className="text-xs text-white/50 mb-1">Effective Mana Available</div>
                                    <div className="text-2xl font-bold text-amber-400">{stats.effectiveManaAvailable}</div>
                                    <div className="text-xs text-white/40 flex items-center gap-1">
                                        <span>{stats.untappedLands} lands</span>
                                        {stats.manaDorks > 0 && (
                                            <>
                                                <span className="text-white/20">+</span>
                                                <span className="text-green-400">{stats.manaDorks} dorks</span>
                                            </>
                                        )}
                                    </div>
                                </div>

                                {/* Hand composition */}
                                <div className="bg-white/5 rounded-lg p-3">
                                    <div className="text-xs text-white/50 mb-2">Hand ({hand.length})</div>
                                    <div className="flex gap-2 text-sm">
                                        <span className="text-amber-400">{stats.landsInHand} lands</span>
                                        <span className="text-white/30">|</span>
                                        <span className="text-cyan-400">{stats.spellsInHand} spells</span>
                                    </div>
                                    {stats.castableSpells > 0 && (
                                        <div className="text-xs text-green-400 mt-1">
                                            {stats.castableSpells} castable now
                                        </div>
                                    )}
                                </div>

                                {/* CMC breakdown */}
                                {Object.keys(stats.handByCmc).length > 0 && (
                                    <div className="bg-white/5 rounded-lg p-3">
                                        <div className="text-xs text-white/50 mb-2">Spells by CMC</div>
                                        <div className="flex gap-1">
                                            {[1, 2, 3, 4, 5, 6, 7].map(cmc => (
                                                <div key={cmc} className="flex flex-col items-center">
                                                    <div
                                                        className={`w-6 h-8 rounded flex items-center justify-center text-xs font-medium ${stats.handByCmc[cmc] ? 'bg-cyan-500/30 text-cyan-300' : 'bg-white/5 text-white/20'}`}
                                                    >
                                                        {stats.handByCmc[cmc] || 0}
                                                    </div>
                                                    <span className="text-[10px] text-white/40 mt-0.5">{cmc === 7 ? '7+' : cmc}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {/* Library stats with Deck Thinning */}
                                <div className="bg-white/5 rounded-lg p-3">
                                    <div className="text-xs text-white/50 mb-1">Library ({stats.totalRemaining})</div>
                                    <div className="text-sm text-white/70">
                                        {stats.landsRemaining} lands remaining
                                    </div>

                                    {/* Land Density - shows thinning effect */}
                                    <div className="mt-2">
                                        <div className="text-xs text-white/50 mb-1 flex items-center justify-between">
                                            <span>Land Density</span>
                                            {stats.initialLandDensity !== stats.currentLandDensity && (
                                                <span className={`text-[10px] ${stats.currentLandDensity < stats.initialLandDensity ? 'text-green-400' : 'text-amber-400'}`}>
                                                    {stats.currentLandDensity < stats.initialLandDensity ? '↓ Thinned' : '↑ Land-heavy'}
                                                </span>
                                            )}
                                        </div>
                                        <div className="flex items-center gap-2 text-xs">
                                            <span className="text-white/40">{stats.initialLandDensity.toFixed(1)}%</span>
                                            <span className="text-white/20">→</span>
                                            <span className={`font-medium ${stats.currentLandDensity < stats.initialLandDensity ? 'text-green-400' : 'text-amber-400'}`}>
                                                {stats.currentLandDensity.toFixed(1)}%
                                            </span>
                                        </div>
                                    </div>

                                    {/* Next draw probability */}
                                    <div className="mt-2">
                                        <div className="text-xs text-white/50 mb-1">Next draw is land</div>
                                        <div className="flex items-center gap-2">
                                            <div className="flex-1 h-2 bg-white/10 rounded-full overflow-hidden">
                                                <div
                                                    className="h-full bg-gradient-to-r from-amber-500 to-amber-400 transition-all"
                                                    style={{ width: `${stats.landDrawProb}%` }}
                                                />
                                            </div>
                                            <span className="text-xs text-amber-400 font-medium">{stats.landDrawProb.toFixed(0)}%</span>
                                        </div>
                                    </div>
                                </div>

                                {/* Cards drawn */}
                                <div className="bg-white/5 rounded-lg p-3">
                                    <div className="text-xs text-white/50 mb-1">Cards Seen</div>
                                    <div className="text-lg font-semibold text-white">
                                        {stats.cardsDrawn} / {deck.reduce((sum, c) => sum + c.quantity, 0)}
                                    </div>
                                </div>

                                {/* Ramp Diagnostics */}
                                {(stats.rampSourcesInPlay > 0 || stats.rampCardsInHand > 0) && (
                                    <div className="bg-gradient-to-br from-purple-500/10 to-amber-500/10 rounded-lg p-3 border border-purple-500/20">
                                        <div className="text-xs text-purple-400 mb-2 font-medium">Ramp Diagnostics</div>
                                        <div className="space-y-1 text-xs">
                                            <div className="flex justify-between items-center">
                                                <span className="text-white/60">Ramp Sources in Play</span>
                                                <span className="text-green-400 font-medium">{stats.rampSourcesInPlay}</span>
                                            </div>
                                            <div className="flex justify-between items-center">
                                                <span className="text-white/60">Ramp Cards in Hand</span>
                                                <span className="text-cyan-400 font-medium">{stats.rampCardsInHand}</span>
                                            </div>
                                            {stats.missedRamp > 0 && (
                                                <div className="flex justify-between items-center pt-1 border-t border-white/10">
                                                    <span className="text-amber-400/80">⚠️ Castable Ramp</span>
                                                    <span className="text-amber-400 font-bold">{stats.missedRamp}</span>
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}

                                {/* Timer */}
                                <div className="bg-white/5 rounded-lg p-3 flex items-center gap-2">
                                    <Timer className="size-4 text-white/40" />
                                    <div className="text-xs text-white/50">Turn {turn}</div>
                                </div>
                            </div>
                        </div>
                    )
                }
            </div >

            {/* Card hover preview */}
            {
                hoveredCard && !selectedCard && (
                    <div className="fixed top-20 right-4 z-40 pointer-events-none">
                        <div className="bg-slate-900/95 backdrop-blur rounded-xl border border-white/20 p-3 shadow-2xl">
                            <div className="text-sm font-semibold text-white mb-1">{hoveredCard.card.name}</div>
                            {!hoveredCard.card.isLand && (
                                <div className="text-xs text-cyan-400">CMC: {hoveredCard.card.cmc}</div>
                            )}
                            {hoveredCard.card.type && (
                                <div className="text-xs text-white/50 mt-1">{hoveredCard.card.type}</div>
                            )}
                            <div className="text-xs text-white/30 mt-2">
                                Double-click to {hoveredCard.zone === 'hand' ? 'play' : hoveredCard.zone === 'battlefield' ? 'tap/untap' : 'select'}
                            </div>
                        </div>
                    </div>
                )
            }

            {/* Card actions */}
            {renderCardActions()}

            {/* Context menu */}
            {renderContextMenu()}
        </div >
    );
}
